import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const NAV_LINKS = [
  ['Features',     '/features'],
  ['Browse roles', '/roles'],
  ['JD Prep',      '/interview-prep'],
  ['ATS Score',    '/ats'],
  ['Study Guides', '/courses'],
  ['Blog',         '/blog'],
  ['Pricing',      '/pricing'],
];

function IconMenu() {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
    </svg>
  );
}
function IconX() {
  return (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}
function IconSun() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  );
}
function IconMoon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );
}

/**
 * Shared public navbar used across all marketing/public pages.
 *
 * Props:
 *   forceLight  — always render the light (scrolled) style, e.g. for pages with a
 *                 white/light hero (Pricing, About, Contact, Browse roles, etc.)
 *                 When false (default) the nav is transparent until the user scrolls.
 */
export default function PublicNavbar({ forceLight = false }) {
  const location = useLocation();
  const [scrolled, setScrolled]   = useState(false);
  const [menuOpen, setMenuOpen]   = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const light = forceLight || scrolled || menuOpen;

  const linkColor = (label) => {
    if (label === 'JD Prep') return '#C1272D';
    return light ? '#334155' : 'rgba(255,255,255,0.82)';
  };
  const linkHover = (label) => {
    if (label === 'JD Prep') return '#A8202B';
    return light ? '#1B3A5C' : '#ffffff';
  };

  return (
    <>
      <style>{`.pub-skip{position:absolute;left:-9999px;top:auto;width:1px;height:1px;overflow:hidden}.pub-skip:focus{position:fixed;left:16px;top:16px;width:auto;height:auto;padding:10px 18px;background:#1B3A5C;color:#fff;font-weight:700;font-size:14px;border-radius:8px;z-index:9999;text-decoration:none;outline:2px solid #C1272D}`}</style>
      <a href="#main-content" className="pub-skip">Skip to main content</a>
      <nav
        className="fixed top-0 left-0 right-0 z-[100] transition-all duration-300"
        style={light ? { background: '#ffffff', boxShadow: '0 1px 0 #E2E8F0' } : {}}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">

          {/* Logo */}
          <Link to="/" className="no-underline flex-shrink-0">
            <span className="text-[22px] font-extrabold tracking-tight" style={{ color: light ? '#1B3A5C' : '#ffffff' }}>
              Centric<span style={{ color: '#C1272D' }}>Q</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-7">
            {NAV_LINKS.map(([label, to]) => (
              <Link key={to} to={to}
                className="no-underline text-[14px] font-medium transition-colors duration-200"
                style={{ color: linkColor(label) }}
                onMouseEnter={e => e.currentTarget.style.color = linkHover(label)}
                onMouseLeave={e => e.currentTarget.style.color = linkColor(label)}>
                {label}
              </Link>
            ))}

            <Link to="/login"
              className="no-underline text-[14px] font-medium transition-colors duration-200"
              style={{ color: light ? '#334155' : 'rgba(255,255,255,0.82)' }}
              onMouseEnter={e => e.currentTarget.style.color = light ? '#1B3A5C' : '#fff'}
              onMouseLeave={e => e.currentTarget.style.color = light ? '#334155' : 'rgba(255,255,255,0.82)'}>
              Sign in
            </Link>

            {/* For Companies */}
            <Link to="/company/login"
              className="no-underline px-4 py-2 rounded-xl text-[13px] font-semibold transition-all duration-200"
              style={{ border: light ? '1.5px solid #1B3A5C' : '1.5px solid rgba(255,255,255,0.45)', color: light ? '#1B3A5C' : 'rgba(255,255,255,0.9)', background: 'transparent' }}
              onMouseEnter={e => { e.currentTarget.style.opacity = '0.75'; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}>
              For Companies
            </Link>

            {/* Start free CTA */}
            <Link to="/register"
              className="no-underline px-5 py-2.5 rounded-xl text-[14px] font-bold text-white transition-all duration-200"
              style={{ background: '#C1272D' }}
              onMouseEnter={e => e.currentTarget.style.background = '#A8202B'}
              onMouseLeave={e => e.currentTarget.style.background = '#C1272D'}>
              Start free
            </Link>
          </div>

          {/* Mobile: hamburger */}
          <div className="flex md:hidden items-center gap-2">
            <button
              className="bg-transparent border-0 cursor-pointer p-1 transition-colors duration-300"
              style={{ color: light ? '#1B3A5C' : '#ffffff' }}
              onClick={() => setMenuOpen(o => !o)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            >
              {menuOpen ? <IconX /> : <IconMenu />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div
          className="fixed top-16 left-0 right-0 z-[99] px-6 pt-5 pb-7 flex flex-col gap-1 border-b"
          style={{ background: '#ffffff', borderColor: '#E2E8F0', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}
        >
          {[...NAV_LINKS, ['Sign in', '/login'], ['For Companies', '/company/login']].map(([label, to]) => (
            <Link key={to} to={to} onClick={() => setMenuOpen(false)}
              className="no-underline py-3 border-b font-medium text-base"
              style={{ color: label === 'JD Prep' ? '#C1272D' : label === 'For Companies' ? '#1B3A5C' : '#334155', borderColor: '#E2E8F0' }}>
              {label}
            </Link>
          ))}
          <Link to="/register" onClick={() => setMenuOpen(false)}
            className="mt-3 text-white no-underline py-3.5 rounded-xl text-[15px] font-bold text-center block"
            style={{ background: '#C1272D' }}>
            Start free — no card needed
          </Link>
        </div>
      )}
    </>
  );
}
